import { useMemo, useCallback, useState } from 'react';
import ProsemirrorRichTextEditor from 'hubspot-prosemirror/prosemirror/ProsemirrorEditor';
import { DOCUMENTS, TOOLBAR_CUSTOMIZATION } from 'hubspot-prosemirror/prosemirror/editorCapabilities/constants';
import { LAYOUT_TYPES } from 'hubspot-prosemirror/prosemirror/editorCapabilities/toolbarCustomization';
import { PASTE_OVERRIDE } from 'hubspot-prosemirror/prosemirror/editorCapabilities';
import useHubSpotRTE from 'hubspot-prosemirror/prosemirror/useHubSpotRTE';
import { createRpcClientV2 } from 'rpc-client-utils';
import portalsHttpClient from 'portals-platform-lib/portalsHttpClient';
import { listEntriesRpc, searchEntriesRpc } from 'deal-plans-lib/__generated__/chirp/com/hubspot/sales/documents/portal/rpc/SalesDocumentIndexPortalRpc';
import { useListSharedSalesDocumentsQuery, useShareSalesDocumentsMutation } from 'deal-plans-lib/__generated__/chirp/com/hubspot/portals/platform/app/rpc/SharedSalesDocumentsRpc.hooks';
import { convertToHtml, hasContent } from 'hubspot-prosemirror/prosemirror/ProsemirrorUtils';
import { getPlaceholderPlugin } from 'hubspot-prosemirror/prosemirror/plugins/PlaceholderPlugin';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import I18n from 'I18n';
import { restoreDocumentLinkAttributes } from '../../utils/restoreDocumentLinkAttributes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const chirpClient = createRpcClientV2({
  hubHttpClient: portalsHttpClient
});
const EditorFrame = styled.div.withConfig({
  displayName: "RichContentBlock__EditorFrame"
})(({
  theme
}) => ({
  '.ProsemirrorEditor__wrapper': {
    padding: '0 !important'
  },
  '.ProseMirror': {
    color: `${theme.color['text-core-default']} !important`
  }
}));
const BASE_EDITOR_CONFIG = {
  BOLD: {},
  ITALIC: {},
  UNDERLINE: {},
  LISTS: {},
  LINK: {},
  EMOJI: {},
  // strip formatting that can't be controlled directly via the editor
  [PASTE_OVERRIDE]: {
    stripTextColor: true,
    stripTextHighlights: true,
    stripTextStyle: true,
    stripTextSize: true
  }
};
export function RichContentBlock({
  description,
  onSave,
  onCancel,
  portalInstanceId,
  dealPlanId,
  // TODO: Remove this prop to make the document attachment feature available
  documentAttachmentEnabled = false
}) {
  const isDocumentAttachmentEnabled = portalInstanceId !== undefined && dealPlanId !== undefined && documentAttachmentEnabled;
  const [shareDocs] = useShareSalesDocumentsMutation();
  const {
    data: sharedDocsData
  } = useListSharedSalesDocumentsQuery({
    variables: {
      queryObjectTypeId: '0-3366',
      queryObjectId: dealPlanId,
      limit: 100,
      offset: 0
    },
    skip: !isDocumentAttachmentEnabled
  });
  const sharedDocumentsMap = useMemo(() => {
    const map = new Map();
    for (const doc of (_sharedDocsData$sales = sharedDocsData === null || sharedDocsData === void 0 ? void 0 : sharedDocsData.salesDocuments) !== null && _sharedDocsData$sales !== void 0 ? _sharedDocsData$sales : []) {
      var _sharedDocsData$sales;
      if (doc.shareLink) {
        map.set(doc.salesDocumentId, doc.shareLink);
      }
    }
    return map;
  }, [sharedDocsData]);
  const mapEntries = useCallback(entries => entries.map(e => {
    var _e$brandId, _e$thumbnailUrl, _e$createdBy$firstNam, _e$createdBy, _e$createdBy$lastName, _e$createdBy2, _e$createdBy$email, _e$createdBy3;
    return {
      contentId: e.id,
      name: e.name,
      documentId: String(e.id),
      brandId: (_e$brandId = e.brandId) !== null && _e$brandId !== void 0 ? _e$brandId : null,
      legalConsentEnabled: null,
      metadata: {
        title: e.name,
        thumbnailUrl: (_e$thumbnailUrl = e.thumbnailUrl) !== null && _e$thumbnailUrl !== void 0 ? _e$thumbnailUrl : '',
        user: {
          firstName: (_e$createdBy$firstNam = (_e$createdBy = e.createdBy) === null || _e$createdBy === void 0 ? void 0 : _e$createdBy.firstName) !== null && _e$createdBy$firstNam !== void 0 ? _e$createdBy$firstNam : '',
          lastName: (_e$createdBy$lastName = (_e$createdBy2 = e.createdBy) === null || _e$createdBy2 === void 0 ? void 0 : _e$createdBy2.lastName) !== null && _e$createdBy$lastName !== void 0 ? _e$createdBy$lastName : '',
          email: (_e$createdBy$email = (_e$createdBy3 = e.createdBy) === null || _e$createdBy3 === void 0 ? void 0 : _e$createdBy3.email) !== null && _e$createdBy$email !== void 0 ? _e$createdBy$email : ''
        }
      }
    };
  }), []);
  const fetchDocumentsFn = useCallback(async () => {
    var _response$results;
    const response = await chirpClient.call(listEntriesRpc, {
      request: {
        entryTypes: ['DOCUMENT'],
        portalInstanceId,
        accountId: portalInstanceId,
        pageLength: 100
      }
    });
    const entries = ((_response$results = response.results) !== null && _response$results !== void 0 ? _response$results : []).filter(e => e._type === 'DocumentEntry');
    return {
      total: entries.length,
      offset: response.offset,
      results: mapEntries(entries)
    };
  }, [portalInstanceId, mapEntries]);
  const searchDocumentsFn = useCallback(async query => {
    var _response$results2;
    const response = await chirpClient.call(searchEntriesRpc, {
      request: {
        query,
        entryTypes: ['DOCUMENT'],
        portalInstanceId,
        accountId: portalInstanceId,
        pageLength: 100
      }
    });
    const entries = ((_response$results2 = response.results) !== null && _response$results2 !== void 0 ? _response$results2 : []).filter(e => e._type === 'DocumentEntry');
    return {
      total: entries.length,
      offset: response.offset,
      results: mapEntries(entries)
    };
  }, [portalInstanceId, mapEntries]);
  const renderDocumentFn = useCallback(async ({
    documentId,
    documentName
  }) => {
    var _result$data;
    const existingLink = sharedDocumentsMap.get(documentId);
    if (existingLink) {
      return {
        url: existingLink,
        text: documentName
      };
    }
    const result = await shareDocs({
      variables: {
        salesDocumentIds: [documentId],
        scopeObjectTypeId: '0-3366',
        scopeObjectId: dealPlanId
      }
    });
    const shareLink = (_result$data = result.data) === null || _result$data === void 0 || (_result$data = _result$data.result) === null || _result$data === void 0 || (_result$data = _result$data.sharedDocuments) === null || _result$data === void 0 || (_result$data = _result$data[0]) === null || _result$data === void 0 ? void 0 : _result$data.shareLink;
    return shareLink ? {
      url: shareLink,
      text: documentName
    } : null;
  }, [shareDocs, dealPlanId, sharedDocumentsMap]);
  const editorConfig = useMemo(() => Object.assign({}, BASE_EDITOR_CONFIG, isDocumentAttachmentEnabled ? {
    [DOCUMENTS]: {
      fetchDocumentsFn,
      searchDocumentsFn,
      renderDocumentFn,
      showIncludeLinkPreview: false
    }
  } : {}, {
    [TOOLBAR_CUSTOMIZATION]: ['BOLD', 'ITALIC', 'UNDERLINE', {
      layoutType: LAYOUT_TYPES.DIVIDER,
      key: 'divider-1'
    }, 'LISTS', {
      layoutType: LAYOUT_TYPES.DIVIDER,
      key: 'divider-2'
    }, 'LINK', 'EMOJI', ...(isDocumentAttachmentEnabled ? [DOCUMENTS] : [])]
  }), [isDocumentAttachmentEnabled, fetchDocumentsFn, searchDocumentsFn, renderDocumentFn]);
  const placeholderPlugins = useMemo(() => [getPlaceholderPlugin(I18n.text('dealPlan.richContentBlock.placeholder'))], []);
  const initialHtml = useMemo(() => restoreDocumentLinkAttributes(description), [description]);
  const [editorState, setEditorState] = useHubSpotRTE(initialHtml, editorConfig);

  // This ensures that when the user has deleted all content, we actually
  // save an empty string with no dangling html tags
  const normalizedHtml = hasContent(editorState) ? convertToHtml(editorState).trim() : '';
  // Compare against what the editor makes of the saved html, not the saved
  // html itself: the editor re-adds attributes the CRM property sanitized away,
  // so the two never match for a description with an inserted document
  const [initialNormalizedHtml] = useState(normalizedHtml);
  const hasUnsavedChanges = normalizedHtml !== initialNormalizedHtml;
  return /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "600",
    children: [/*#__PURE__*/_jsx(EditorFrame, {
      children: /*#__PURE__*/_jsx(View, {
        borderWidth: "100",
        borderStyle: "solid",
        borderColor: "border-core-default",
        borderRadius: "100",
        padding: "300",
        backgroundColor: "fill-field-default",
        children: /*#__PURE__*/_jsx(ProsemirrorRichTextEditor, {
          editorState: editorState,
          setEditorState: setEditorState,
          editorConfig: editorConfig,
          customPlugins: placeholderPlugins
        })
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      marginBlockStart: "200",
      gap: "200",
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        size: "extra-small",
        "data-test-id": "description-save-button",
        disabled: !hasUnsavedChanges,
        onClick: () => {
          void onSave(normalizedHtml);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "dealPlan.richContentBlock.save"
        })
      }), onCancel && /*#__PURE__*/_jsx(Button, {
        "data-test-id": "description-cancel-button",
        use: "unstyled",
        onClick: onCancel,
        children: /*#__PURE__*/_jsx(Text, {
          use: "disabled",
          typography: "detail-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "dealPlan.richContentBlock.cancel"
          })
        })
      })]
    })]
  });
}